/**
 * A 422 from flymsg-node's `POST /api/v3/subscriptions/preview-change` is
 * NOT always a rejected coupon (issue #601 round-2 QA R-01/R-05). That same
 * route also 422s for reasons that have nothing to do with the coupon that
 * was sent — "Plan '…' is not currently available.", "Plan '…' is not
 * billable through Stripe.", "No active plan found for identifier '…'." —
 * and, elsewhere on the checkout surface, a company-managed refusal. Only
 * the coupon's OWN rejection (`INVALID_DISCOUNT_CODE_MESSAGE` in
 * flymsg-node's `couponService.ts`, currently "This discount code is not
 * valid.") may ever invalidate the code the user typed; every other 422
 * must fall through to the ordinary previewFailed/Retry state so a VALID
 * code is never dropped and mislabeled "not valid" just because the plan
 * itself couldn't be previewed for an unrelated reason.
 *
 * flymsg-node wraps an `HttpError` as `{success:false, message}`, which the
 * response envelope then wraps again as `{result: {success:false,
 * message}}` (see flymsg-node's `errorHandler.ts`/`envelope.ts`) — so on the
 * Angular `HttpErrorResponse` this lands at `error.error.result.message`.
 * `error.error.message` is read too, in case a future envelope change (or a
 * different surface reusing this same check) skips that extra `result`
 * wrap.
 */
export function isNodeCouponRejection(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  if (status !== 422) {
    return false;
  }

  const body = (error as { error?: { result?: { message?: unknown }; message?: unknown } } | null)?.error;
  const message = body?.result?.message ?? body?.message;
  if (typeof message !== "string") {
    return false;
  }

  // Matched on substring rather than the exact constant so a wording tweak
  // upstream (e.g. dropping the trailing period) doesn't silently stop
  // being recognised — but specific enough that it can never match
  // "Plan '…' is not currently available.", "…is not billable through
  // Stripe.", "No active plan found…", or a company-managed refusal.
  const normalized = message.toLowerCase();
  return normalized.includes("discount code") && normalized.includes("valid");
}
